import {Plural, Trans} from '@lingui/macro';
import React, {FC, ReactNode, RefObject, useState} from 'react';
import styled from 'styled-components';

import {
  AsyncHandler,
  AsyncHandlerOf,
  Handler,
  HandlerOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import imgSrc from '../../../../assets/images/integrations/hubspot16.svg';
import {CrmHubspotFieldModel} from '../../../../models/api/crmHubspotFieldModel';
import {CrmHubspotMetadataModel} from '../../../../models/api/crmHubspotMetadataModel';
import {
  CrmSyncErrorModel,
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../models/api/crmSyncModel';
import {CustomFieldModel} from '../../../../models/api/customFieldModel';
import {SyncJobModel} from '../../../../models/api/jobModel';
import {CrmPreviewParentPanelId} from '../../../../store/panels/contacts/contactsManager/crmPreview/crmPreviewActions';
import {CrmLastSyncedBannerContainer} from '../../../common/crm/crmLastSyncedBannerContainer';
import {CrmSyncErrorsBannerContainer} from '../banners/crmSyncErrorsBannerContainer';
import {CrmSyncingBanner} from '../banners/crmSyncingBanner';
import {CrmToFrontCustomFieldMapping} from '../common/crmSyncHelpers';
import {CrmSyncInfoBox} from '../common/crmSyncInfoBox';
import {CrmSyncStepOneContainer} from '../common/crmSyncStepOneContainer';
import {CrmSyncErrorsPageContainer} from '../common/errors/crmSyncErrorsPageContainer';
import {CrmSyncDialogContainer} from '../crmSyncDialogContainer';
import {HubspotCrmSyncMappingStep} from './hubspotCrmSyncMappingStep';
import {HubspotMaxAccountsCountable} from './hubspotHelpers';

/*
 * Props.
 */

export interface HubspotCrmSyncDialogProps {
  panelId: CrmPreviewParentPanelId;
  hasCrmSyncFeature: boolean;
  hasExpiredCredentials: boolean;
  activeSyncJob?: SyncJobModel;
  hasInvalidMappedField: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  isUpdatingCrmSync: boolean;
  crmSync?: CrmSyncModel;
  hubspotFields?: ReadonlyArray<CrmHubspotFieldModel>;
  hubspotMetadata?: CrmHubspotMetadataModel;
  username?: string;
  isConnected: boolean;
  customFields: CustomFieldModel[];
  contentWrapperRef?: RefObject<HTMLDivElement>;
  mappedFields: readonly CrmToFrontCustomFieldMapping[];
  shouldStartOnErrorsPage?: boolean;
  disableCrmSyncAsync: AsyncHandlerOf<number>;
  onUpdateCrmSyncAsync: AsyncHandler;
  onRequestClose: Handler;
  onRemoveMappedField: HandlerOf<string>;
  onAddMappedField: HandlerOf<CrmToFrontCustomFieldMapping>;
  onUpdateMappedField: (crmFieldId: string, customFieldId?: number, newCrmFieldId?: string) => void;
}

/*
 * Style.
 */

const StyledInfoBoxUl = styled.ul`
  padding: 0;
  margin: 0 0 0 -10px;
`;

const StyledInfoBoxLi = styled.li`
  padding-left: 10px;
  &:not(:last-child) {
    margin-bottom: 6px;
  }
`;

/*
 * Component.
 */

export const HubspotCrmSyncDialog: FC<HubspotCrmSyncDialogProps> = (props) => {
  const {
    panelId,
    isAdmin,
    isLoading,
    hasCrmSyncFeature,
    username,
    hubspotMetadata,
    hubspotFields,
    isConnected,
    hasExpiredCredentials,
    crmSync,
    contentWrapperRef,
    mappedFields,
    activeSyncJob,
    shouldStartOnErrorsPage,
    disableCrmSyncAsync,
    onUpdateCrmSyncAsync,
    onRequestClose,
    onRemoveMappedField,
    onAddMappedField,
    onUpdateMappedField,
  } = props;

  const isConnectedAndValid = isConnected && !hasExpiredCredentials;
  // if we are logged in properly, but are still missing fields and metadata then we show loading
  const isSectionsLoading = isConnectedAndValid && (isLoading || !hubspotFields || !hubspotMetadata);
  const [isShowingErrorsPage, setIsShowingErrorsPage] = useState(Boolean(shouldStartOnErrorsPage));

  const maybeRenderCrmSyncSteps = () => {
    if (isShowingErrorsPage) {
      return null;
    }
    return (
      <>
        <CrmSyncStepOneContainer
          crmLogoImgSrc={imgSrc}
          hasExpiredCredentials={hasExpiredCredentials}
          isConnected={isConnected}
          crmSyncSource={CrmSyncSourcesEnum.HUBSPOT}
          username={username}
        />

        <HubspotCrmSyncMappingStep
          panelId={panelId}
          crmSyncId={crmSync?.id}
          isAdmin={isAdmin}
          hidePreview={computeHasNoAccounts(hubspotMetadata)}
          isDisabled={computeIsMappingStepDisabled(props)}
          onRemoveMappedField={onRemoveMappedField}
          onAddMappedField={onAddMappedField}
          onUpdateMappedField={onUpdateMappedField}
          isLoading={isSectionsLoading}
          mappedFields={mappedFields}
          hubspotFields={hubspotFields}
          hubspotMetadata={hubspotMetadata}
        />
      </>
    );
  };

  const renderInfoBox = (onInfoBoxRequestClose: Handler) => (
    <CrmSyncInfoBox
      isAdmin={isAdmin}
      crmSyncSource={CrmSyncSourcesEnum.HUBSPOT}
      onRequestClose={onInfoBoxRequestClose}
      title={<Trans id="accounts-crm-hubspot-dialog-infobox-title">1-way sync from Hubspot to Front</Trans>}
      renderBody={renderInfoBoxBody}
    />
  );

  return (
    <CrmSyncDialogContainer
      status={crmSync?.status}
      numAccounts={hubspotMetadata?.allAccountsCount}
      contentWrapperRef={contentWrapperRef}
      title={renderHubspotDialogTitle(isShowingErrorsPage)}
      source={CrmSyncSourcesEnum.HUBSPOT}
      onSyncClick={onUpdateCrmSyncAsync}
      onBackClick={isShowingErrorsPage ? () => setIsShowingErrorsPage(false) : undefined}
      onDisableSyncClick={() => {
        if (!crmSync) {
          return;
        }
        disableCrmSyncAsync(crmSync.id);
      }}
      onRequestClose={onRequestClose}
      syncButtonText={maybeRenderCustomSyncButtonText(crmSync, hubspotMetadata)}
      isSyncDisabled={computeIsSyncDisabled(isSectionsLoading, props)}
      hasCrmSyncFeature={hasCrmSyncFeature}
      renderInfoBox={isShowingErrorsPage ? undefined : renderInfoBox}
      renderBanners={() =>
        renderBanners(crmSync, activeSyncJob, isShowingErrorsPage, () => setIsShowingErrorsPage(true))
      }
    >
      {maybeRenderCrmSyncSteps()}
      {maybeRenderErrorsPage(
        isShowingErrorsPage,
        () => setIsShowingErrorsPage(false),
        crmSync?.externalUrl,
        crmSync?.errors,
      )}
    </CrmSyncDialogContainer>
  );
};

/*
 * Helpers.
 */

function renderInfoBoxBody(defaultBody: ReactNode) {
  return (
    <StyledInfoBoxUl>
      <StyledInfoBoxLi>{defaultBody}</StyledInfoBoxLi>
      <StyledInfoBoxLi>
        <Trans id="accounts-crm-dialog-infobox-hubspot-max-account-info">
          If you have more than 100,000 accounts in Hubspot, Front will only sync the first 100,000.
        </Trans>
      </StyledInfoBoxLi>
    </StyledInfoBoxUl>
  );
}

function maybeRenderErrorsPage(
  isShowingErrorsPage: boolean,
  onBackToMainPage: Handler,
  externalUrl?: string,
  errors?: ReadonlyArray<CrmSyncErrorModel>,
) {
  if (!isShowingErrorsPage) {
    return null;
  }
  return (
    <CrmSyncErrorsPageContainer
      source={CrmSyncSourcesEnum.HUBSPOT}
      errors={errors ?? []}
      externalUrl={externalUrl}
      onBackToMainPage={onBackToMainPage}
    />
  );
}

function renderBanners(
  crmSync: CrmSyncModel | undefined,
  activeSyncJob: SyncJobModel | undefined,
  isShowingErrorsPage: boolean,
  onViewErrorsClick: Handler,
) {
  if (isShowingErrorsPage || crmSync?.status === CrmSyncStatusesEnum.DISABLED) {
    return null;
  }
  if (activeSyncJob) {
    return <CrmSyncingBanner job={activeSyncJob} />;
  }
  return (
    <>
      <CrmLastSyncedBannerContainer
        source={CrmSyncSourcesEnum.HUBSPOT}
        resourceType={CrmSyncIntegrationResourceTypesEnum.ACCOUNT}
        shouldBoldDates
      />
      {crmSync && <CrmSyncErrorsBannerContainer syncId={crmSync.id} onViewErrorsClick={onViewErrorsClick} />}
    </>
  );
}

function computeIsMappingStepDisabled(props: HubspotCrmSyncDialogProps) {
  const {hasCrmSyncFeature, crmSync, isConnected, activeSyncJob, hasExpiredCredentials} = props;
  return (
    !hasCrmSyncFeature || !crmSync || !isConnected || Boolean(activeSyncJob) || Boolean(hasExpiredCredentials)
  );
}

function computeIsSyncDisabled(isSectionsLoading: boolean, props: HubspotCrmSyncDialogProps) {
  const {
    isConnected,
    isAdmin,
    activeSyncJob,
    hasExpiredCredentials,
    isUpdatingCrmSync,
    hasInvalidMappedField,
    hubspotMetadata,
  } = props;
  return (
    isUpdatingCrmSync ||
    !isConnected ||
    hasExpiredCredentials ||
    !isAdmin ||
    Boolean(activeSyncJob) ||
    isSectionsLoading ||
    hasInvalidMappedField ||
    computeHasNoAccounts(hubspotMetadata)
  );
}

function renderHubspotDialogTitle(isShowingErrorsPage: boolean) {
  if (isShowingErrorsPage) {
    return <Trans id="accounts-crm-hubspot-dialog-errors-title">Syncing errors</Trans>;
  }
  return <Trans id="accounts-crm-hubspot-dialog-title">Sync accounts from Hubspot</Trans>;
}

function computeHasNoAccounts(hubspotMetadata?: CrmHubspotMetadataModel) {
  if (!hubspotMetadata) {
    return false;
  }
  return hubspotMetadata.allAccountsCount === 0;
}

function maybeRenderCustomSyncButtonText(crmSync?: CrmSyncModel, hubspotMetadata?: CrmHubspotMetadataModel) {
  // no custom text needed since we are within the countable amount
  if (!hubspotMetadata || hubspotMetadata.allAccountsCount <= HubspotMaxAccountsCountable) {
    return undefined;
  }

  const displayNumber = HubspotMaxAccountsCountable.toLocaleString();
  // we have more accounts in hubspot than we were able to count
  if (crmSync?.status === CrmSyncStatusesEnum.DISABLED) {
    return (
      <Plural
        id="accounts-crm-dialog-sync-num-accounts-or-more"
        value={HubspotMaxAccountsCountable}
        one={`Sync ${displayNumber}+ account`}
        other={`Sync ${displayNumber}+ accounts`}
      />
    );
  }

  return (
    <Plural
      id="accounts-crm-dialog-sync-num-accounts-or-more-now"
      value={HubspotMaxAccountsCountable}
      one={`Sync ${displayNumber}+ account now`}
      other={`Sync ${displayNumber}+ accounts now`}
    />
  );
}
